import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { URLSearchParams, Response } from '@angular/http';

import { Api } from './api.service';
import { CrudInterface } from './crud.interface';
import { PAGINATION_NUMBER } from './constants.service';
import { Tracker, Position, Task, User, Subscription, TrackerAdmin, ListResult, ITrackerFilter } from '../entities/index';
import { Log } from '../utils/index';
import { truncate } from 'lodash';

declare let moment: any;

@Injectable()
export class TrackerService implements CrudInterface<Tracker> {

  constructor(protected http: Api) {}

  @Log('green')
  list(search: string = null, page: number = 1, limit: number = PAGINATION_NUMBER, filters?: ITrackerFilter): Observable<ListResult<Tracker>> {
  const params = new URLSearchParams();
    if (search) {
      params.set('search', String(search));
    }
    if (page) {
      params.set('page', String(page));
    }
    if (limit) {
      params.set('limit', String(limit));
    }
    if(filters) {
      params.setAll(this.http.buildUrlSearchParams(filters));
    }

    return this.http.get('tracker', { search: params });
  }

  @Log('green')
  get(id: number): Observable<Tracker> {
    return this.http.get(`tracker/${id}`).map((res: Tracker) => this._sanitizeTracker(res));
  }

  @Log('green')
  create(tracker: Tracker): Observable<Tracker> {
    return this.http
               .post('tracker', this._sanitizeForSave(tracker))
               .map((res: Tracker) => this._sanitizeTracker(res));
  }

  @Log('green')
  update(tracker: Tracker): Observable<Tracker> {
    const trackerId = tracker.id;
    return this.http
               .put(`tracker/${trackerId}`, this._sanitizeForSave(tracker))
               .map((res: Tracker) => this._sanitizeTracker(res));
  }

  save(tracker: Tracker): Observable<Tracker> {
    const isNew = undefined === tracker.id;
    if (isNew) {
      return this.create(tracker);
    } else {
      return this.update(tracker);
    }
  }

  @Log('green')
  replace(trackerId: number, imei: string): Observable<Tracker> {
    return this.http
               .put(`tracker/${trackerId}`, {imei: imei})
               .map((res: Tracker) => this._sanitizeTracker(res));
  }

  @Log('green')
  remove(tracker: Tracker): Observable<Response> {
    const trackerId = tracker.id;
    return this.http.delete(`tracker/${trackerId}`);
  }

  @Log('green')
  export(trackerId: number, dateFrom: string, dateTo: string): Observable<string> {
    return this.http.downloadFile(`/tracker/${trackerId}/export?start=${dateFrom}&end=${dateTo}`, 'text/csv');
  }

  @Log('green')
  findOneByTrackerAdmin(trackerAdmin: TrackerAdmin): Observable<Tracker> {
    const params = new URLSearchParams();
    params.set('imei', String(trackerAdmin.imei));
    return this.http.get('tracker', { search: params })
                    .map((res: ListResult<Tracker>) => {
                        return res.total === 1 ? res.items[0] : new Tracker();
                    });
  }

  @Log('green')
  findAllOwnedByUser(user: User): Observable<Tracker[]> {
    const params = new URLSearchParams();
    params.set('user_id', String(user.id));
    params.set('limit', '1000');
    return this.http.get('tracker', { search: params })
                    .map((res: ListResult<Tracker>) => {
                        return res.items;
                    });
  }

  @Log('green')
  findAllLinkedToSubscription(subscription: Subscription): Observable<Tracker[]> {
    const params = new URLSearchParams();
    params.set('subscription_id', String(subscription.id));
    params.set('limit', '1000');
    return this.http.get('tracker', { search: params })
                    .map((res: ListResult<Tracker>) => {
                        return res.items;
                    });
  }

  @Log('green')
  transfertToUser(tracker: Tracker, user: User): Observable<Tracker> {
    const trackerId = tracker.id;
    return this.http.post(`tracker/${trackerId}/transfer`, { user_id: user.id });
  }

  @Log('green')
  registerTracker(user: User, trackerData: any): Observable<Tracker> {
    trackerData.user_id = user.id;
    return this.http.post(`tracker/register`, trackerData);
  }

  @Log('green')
  getPositions(trackerId: number, filters: any = {}): Observable<Position[]> {
    return this.http.get(`tracker/${trackerId}/position`, { search: this.http.buildUrlSearchParams(filters) });
  }

  @Log('green')
  getAlerts(trackerId: number, dateParameters: any = {}) {
    return this.http.get(`tracker/${trackerId}/alerts`, { search: this.http.buildUrlSearchParams(dateParameters) });
  }

  @Log('green')
  reset(trackerId: number): Observable<any> {
    return this.http.post(`tracker/${trackerId}/reset`, null);
  }

  @Log('green')
  resetFactory(trackerId: number): Observable<any> {
    return this.http.post(`tracker/${trackerId}/reset-factory`, null);
  }

  @Log('green')
  changeMode(trackerId: number, mode: string): Observable<any> {
    return this.http.post(`tracker/${trackerId}/mode`, {mode: mode});
  }

  @Log('green')
  getVersion(trackerId: number): Observable<any> {
    return this.http.post(`tracker/${trackerId}/version`, null);
  }

  @Log('green')
  resetTimezone(trackerId: number): Observable<any> {
    return this.http.post(`tracker/${trackerId}/timezone`, null);
  }

  @Log('green')
  customCommand(trackerId: number, command: string): Observable<any> {
    return this.http.post(`tracker/${trackerId}/custom-command`, {command: command});
  }

  @Log('green')
  resetCallUsage(trackerId: number): Observable<any> {
    return this.http
               .put(`tracker/${trackerId}`, {
                 call_usage: 0,
                 call_notification: 0
               })
               .map((res: Tracker) => this._sanitizeTracker(res));
  }

  _sanitizeForSave(tracker: Tracker): any {
    const expiration_date = (tracker.expiration_date !== '') ? tracker.expiration_date : null;
    return {
      sim: tracker.sim,
      expiration_date: expiration_date,
      geofence_mode: tracker.geofence_mode,
      sos_mode: tracker.sos_mode,
      spy_sos: tracker.spy_sos,
      call_directly: tracker.call_directly,
      enable_ai: tracker.enable_ai,
      enable_ai_spec: tracker.enable_ai_spec,
      had_subscription: tracker.had_subscription,
      need_upgrade: tracker.need_upgrade,
      signature_pkey: tracker.signature_pkey,
      has_activity_tracking: tracker.has_activity_tracking,
    };
  }
  _sanitizeTracker(tracker: Tracker): Tracker {
    const patched: Tracker = _.clone(tracker);
    patched.expiration_date = patched.expiration_date && moment(patched.expiration_date).format('YYYY-MM-DD[T]HH:mm:ss');
    return patched;
  }
}



// WEBPACK FOOTER //
// /builds/weenect1/backofficev3/src/app/services/tracker.service.ts